import React, { useEffect, useState } from "react";
import { apiService } from "../services/api";
import { SiteNotice as SiteNoticeData } from "../types/api";

// Environment banner shown at the top of every page.
//
// The content comes from the server (GET /api/v1/site-notice) rather than from
// build-time configuration, because the same binary and the same embedded UI
// bundle are deployed to both the dev and production hosts — the frontend has no
// way to know which one it is running on, and guessing from window.location would
// break the first time a host is renamed.
//
// The notice is decoration: if the request fails, nothing is rendered and the app
// carries on. A banner that could take the whole site down with it would be a bad
// trade for a warning message.
export function SiteNotice() {
  const [notice, setNotice] = useState<SiteNoticeData | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getSiteNotice()
      .then((data) => {
        if (!cancelled) setNotice(data);
      })
      .catch(() => {
        // Deliberately silent — see above.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!notice?.enabled || !notice.message) return null;

  const danger = notice.level === "danger";
  const palette = danger
    ? "bg-red-50 border-red-500 text-red-900"
    : "bg-amber-50 border-amber-500 text-amber-900";
  const linkPalette = danger
    ? "text-red-900 decoration-red-400 hover:decoration-red-900"
    : "text-amber-900 decoration-amber-500 hover:decoration-amber-900";

  return (
    <div
      // role="status" rather than "alert": this is ambient context about the
      // environment, not an urgent interruption, so it should not steal focus or
      // preempt whatever a screen reader is currently saying.
      role="status"
      className={`border-b-4 ${palette}`}
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 sm:px-6 lg:px-8">
        <span aria-hidden="true" className="text-lg leading-none">
          {danger ? "⛔" : "⚠️"}
        </span>
        <span className="text-sm font-semibold">{notice.message}</span>
        {notice.link_url && (
          <a
            href={notice.link_url}
            className={`text-sm font-bold underline underline-offset-2 ${linkPalette}`}
          >
            {notice.link_label || notice.link_url}
          </a>
        )}
      </div>
    </div>
  );
}
